'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import type { Order, Product, OccasionPackage } from '@/data/mockData';
import { fetchLiveProducts, fetchLiveCategories, fetchProductBySlug, fetchLiveHampers, fetchLiveHamperCategories, type HamperCategoryItem } from '@/services/api';

export interface CartItem {
  id: string;
  productId: string;
  name: string;
  tamilName?: string;
  image: string;
  weight: string;
  unitPrice: number;
  quantity: number;
}

export interface CategoryItem {
  id: string;
  name: string;
  count: number;
  slug?: string;
  description?: string;
  image?: string;
  isActive?: boolean;
}

export type { HamperCategoryItem };

interface CommerceContextType {
  cart: CartItem[];
  addToCart: (item: Omit<CartItem, 'id'>) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, delta: number) => void;
  clearCart: () => void;
  cartCount: number;
  cartSubtotal: number;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  isCheckoutOpen: boolean;
  setIsCheckoutOpen: (open: boolean) => void;
  isRfqOpen: boolean;
  setIsRfqOpen: (open: boolean) => void;
  rfqInitialData?: any;
  openRfqWithData: (data: any) => void;
  isTrackingOpen: boolean;
  setIsTrackingOpen: (open: boolean) => void;
  isInvoiceOpen: boolean;
  setIsInvoiceOpen: (open: boolean) => void;
  invoiceOrder: Order | null;
  openInvoice: (order: Order) => void;
  isAccountOpen: boolean;
  setIsAccountOpen: (open: boolean) => void;
  isContactOpen: boolean;
  setIsContactOpen: (open: boolean) => void;
  activeView: 'store' | 'admin';
  setActiveView: (view: 'store' | 'admin') => void;

  // Live Catalog State synchronized with Server and Admin
  products: Product[];
  categories: CategoryItem[];
  hamperCategories: HamperCategoryItem[];
  hampers: OccasionPackage[];
  orders: Order[];
  addPlacedOrder: (order: Order) => void;
  isLoadingCatalog: boolean;
  refreshCatalog: () => Promise<void>;
  getProductBySlug: (slug: string) => Promise<Product | undefined>;
}

const CommerceContext = createContext<CommerceContextType | undefined>(undefined);

export function CommerceProvider({ children }: { children: React.ReactNode }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [hamperCategories, setHamperCategories] = useState<HamperCategoryItem[]>([]);
  const [hampers, setHampers] = useState<OccasionPackage[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(true);

  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isRfqOpen, setIsRfqOpen] = useState(false);
  const [rfqInitialData, setRfqInitialData] = useState<any>(null);
  const [isTrackingOpen, setIsTrackingOpen] = useState(false);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState<Order | null>(null);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [activeView, setActiveView] = useState<'store' | 'admin'>('store');

  const refreshCatalog = async () => {
    try {
      const [liveProds, liveCats, liveHampers, liveHamperCats] = await Promise.all([
        fetchLiveProducts(),
        fetchLiveCategories(),
        fetchLiveHampers('all'),
        fetchLiveHamperCategories('all')
      ]);
      setProducts(Array.isArray(liveProds) ? liveProds : []);
      setCategories(Array.isArray(liveCats) ? liveCats : []);
      setHampers(Array.isArray(liveHampers) ? liveHampers : []);
      setHamperCategories(Array.isArray(liveHamperCats) ? liveHamperCats : []);
    } catch (e) {
      console.warn('Failed to refresh catalog from server:', e);
    }
  };

  const getProductBySlug = async (slug: string): Promise<Product | undefined> => {
    const existing = products.find(p => p.slug === slug);
    if (existing) return existing;
    const fetched = await fetchProductBySlug(slug);
    if (fetched) {
      setProducts(prev => {
        if (!prev.some(p => p.id === fetched.id || p.slug === fetched.slug)) {
          return [...prev, fetched];
        }
        return prev;
      });
      return fetched;
    }
    return undefined;
  };

  const addPlacedOrder = (newOrder: Order) => {
    setOrders(prev => {
      const filtered = prev.filter(o => o.orderNumber !== newOrder.orderNumber && o.id !== newOrder.id);
      const updated = [newOrder, ...filtered];
      try {
        localStorage.setItem('elavarasi_user_orders', JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  };

  useEffect(() => {
    setIsLoadingCatalog(true);
    refreshCatalog().finally(() => setIsLoadingCatalog(false));

    // Fast retry after 1.5s in case backend was still booting up on initial page render
    const startupRetry = setTimeout(() => {
      refreshCatalog();
    }, 1500);

    // Load saved orders from localStorage
    try {
      const savedOrders = localStorage.getItem('elavarasi_user_orders');
      if (savedOrders) {
        setOrders(JSON.parse(savedOrders));
      }
    } catch (e) {}

    // Auto-sync when user refocuses tab, changes visibility, or periodic 10s polling
    const handleSync = () => {
      refreshCatalog();
    };
    window.addEventListener('focus', handleSync);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshCatalog();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('storage', handleSync);
    const interval = setInterval(refreshCatalog, 10000);

    return () => {
      clearTimeout(startupRetry);
      window.removeEventListener('focus', handleSync);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('storage', handleSync);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('elavarasi_cart');
      if (saved) {
        setCart(JSON.parse(saved));
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('elavarasi_cart', JSON.stringify(cart));
    } catch (e) {
      console.error(e);
    }
  }, [cart]);

  const addToCart = (item: Omit<CartItem, 'id'>) => {
    const id = `${item.productId}-${item.weight}`;
    setCart((prev) => {
      const existing = prev.find((i) => i.id === id);
      if (existing) {
        return prev.map((i) =>
          i.id === id ? { ...i, quantity: i.quantity + item.quantity } : i
        );
      }
      return [...prev, { ...item, id }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (id: string) => {
    setCart((prev) => prev.filter((i) => i.id !== id));
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((i) => {
          if (i.id === id) {
            const newQty = i.quantity + delta;
            return newQty > 0 ? { ...i, quantity: newQty } : null;
          }
          return i;
        })
        .filter(Boolean) as CartItem[]
    );
  };

  const clearCart = () => setCart([]);

  const openRfqWithData = (data: any) => {
    setRfqInitialData(data);
    setIsRfqOpen(true);
  };

  const openInvoice = (order: Order) => {
    setInvoiceOrder(order);
    setIsInvoiceOpen(true);
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartSubtotal = cart.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);

  return (
    <CommerceContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartCount,
        cartSubtotal,
        isCartOpen,
        setIsCartOpen,
        isCheckoutOpen,
        setIsCheckoutOpen,
        isRfqOpen,
        setIsRfqOpen,
        rfqInitialData,
        openRfqWithData,
        isTrackingOpen,
        setIsTrackingOpen,
        isInvoiceOpen,
        setIsInvoiceOpen,
        invoiceOrder,
        openInvoice,
        isAccountOpen,
        setIsAccountOpen,
        isContactOpen,
        setIsContactOpen,
        activeView,
        setActiveView,
        products,
        categories,
        hamperCategories,
        hampers,
        orders,
        addPlacedOrder,
        isLoadingCatalog,
        refreshCatalog,
        getProductBySlug,
      }}
    >
      {children}
    </CommerceContext.Provider>
  );
}

export function useCommerce() {
  const context = useContext(CommerceContext);
  if (!context) {
    throw new Error('useCommerce must be used within a CommerceProvider');
  }
  return context;
}
